const API_URL = import.meta.env.VITE_API_URL || '';

/** Print the online (base) Shopify price, ignoring every market catalog. */
export const ONLINE_PRICE_VALUE = 'online';

/**
 * The market catalogs that cover a given location.
 *
 * A catalog whose market uses an ALL location condition covers every store without naming
 * any, so it matches on `coversAllLocations` rather than on the location list. A catalog
 * that is neither location-listed nor ALL is scoped by region rather than by store and
 * cannot be tied to a printing location, so it is left out.
 *
 * @param {Array<{locationGids: string[], coversAllLocations: boolean}>} catalogs - From POST /api/app/products/search
 * @param {string|null} locationGid - The selected Shopify Location GID
 * @returns {Array} The subset of catalogs that apply, empty when no location is selected.
 */
export function catalogsForLocation(catalogs, locationGid) {
    if (!locationGid) return [];

    return (catalogs || []).filter((catalog) => catalog?.coversAllLocations || (catalog?.locationGids || []).includes(locationGid));
}

/**
 * Translate the Price catalog selection into the pricing arguments the API expects.
 *
 * Kept in one place because three call sites need it (product search, the PDF URL and the
 * Zebra print job) and they must agree on what prints.
 *
 * The online sentinel carries a real value rather than an empty string, so no caller can
 * turn an intentional "online" choice into a catalog with a falsy check.
 *
 * @param {string} priceSelection - 'online' or a Shopify PriceList GID
 * @returns {string|null} The price list to print from, or null for the online price.
 */
export function priceListIdFor(priceSelection) {
    return priceSelection && priceSelection !== ONLINE_PRICE_VALUE ? priceSelection : null;
}

/**
 * The catalog to select by default for a location.
 *
 * One applicable catalog is the store's price, so it is chosen outright. Where several
 * apply the choice is genuinely the operator's and nothing is selected, which blocks
 * printing until they pick. No applicable catalog means the online price.
 *
 * @param {Array} catalogs - From the search response
 * @param {string|null} locationGid
 * @returns {string} A price list GID, ONLINE_PRICE_VALUE, or '' when a choice is required.
 */
export function defaultPriceSelectionFor(catalogs, locationGid) {
    const applicable = catalogsForLocation(catalogs, locationGid);

    if (applicable.length === 1) return applicable[0].priceListId;
    if (applicable.length === 0) return ONLINE_PRICE_VALUE;

    return '';
}

/**
 * The price that will print for a variant under the current selection.
 *
 * @param {Object} variant - From the search response: { defaultPrice, catalogPrices }
 * @param {string} priceSelection
 * @returns {{amount: number|string|null, isCatalog: boolean}}
 */
export function priceForSelection(variant, priceSelection) {
    const priceListId = priceListIdFor(priceSelection);
    const catalogAmount = priceListId ? variant?.catalogPrices?.[priceListId] : undefined;

    if (catalogAmount === undefined || catalogAmount === null) {
        return { amount: variant?.defaultPrice ?? null, isCatalog: false };
    }

    return { amount: catalogAmount, isCatalog: true };
}

export function buildProductLabelsPdfUrl({ skus, quantitiesBySku, skuType, priceListId }) {
    const queryParams = new URLSearchParams();

    (skus || []).forEach((sku) => {
        const qty = quantitiesBySku?.[sku] || 1;
        queryParams.append(sku, qty.toString());
    });

    queryParams.append('skuType', skuType);

    // Prices from this Market catalog. Omitted means the online price prints.
    if (priceListId) {
        queryParams.append('priceListId', priceListId);
    }

    return `${API_URL}/product-labels?${queryParams.toString()}`;
}

export function countTotalLabels({ skus, quantitiesBySku }) {
    return (skus || []).reduce((sum, sku) => sum + (quantitiesBySku?.[sku] || 1), 0);
}

/**
 * Format a price the same way the printed label does, so the table and the tag agree.
 *
 * @param {number|string|null} amount
 * @returns {string}
 */
export function formatLabelPrice(amount) {
    const parsed = Number(amount);
    if (!Number.isFinite(parsed)) return '$0.00';
    return `$${parsed.toFixed(2)}`;
}

/**
 * True when the price that will print differs from the Shopify default price, i.e. the
 * chosen Market catalog is overriding it.
 *
 * @param {number|string|null} printedAmount
 * @param {number|string|null} defaultAmount
 * @returns {boolean}
 */
export function hasCatalogPriceOverride(printedAmount, defaultAmount) {
    const printed = Number(printedAmount);
    const fallback = Number(defaultAmount);

    if (!Number.isFinite(printed) || !Number.isFinite(fallback)) return false;

    return printed.toFixed(2) !== fallback.toFixed(2);
}
